import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogCloseIconSx,
  harmonateSectionTitleSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAccentLinkSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const emptyLine = () => ({
  lineItemDescription: '',
  quantity: '',
  lineItemRate: '',
  lineItemTotal: '',
  productCode: '',
});

const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

const sumLineTotals = (lineRows) =>
  roundMoney(
    (lineRows || []).reduce((sum, line) => {
      const total = Number(line.lineItemTotal);
      return sum + (Number.isFinite(total) ? total : 0);
    }, 0)
  );

const InvoiceManualEntryDialog = ({
  open,
  onClose,
  tenantId,
  vendors,
  businessUnits,
  businessUnitId: selectedBusinessUnitId = '',
  businessUnitName: selectedBusinessUnitName = '',
  invoiceId = null,
  onSaved,
  showSuccess,
  showError,
}) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const isEditMode = Boolean(invoiceId);
  const [submitting, setSubmitting] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [originalHeader, setOriginalHeader] = useState(null);
  const [originalLineIds, setOriginalLineIds] = useState([]);
  const [loadedBusinessUnit, setLoadedBusinessUnit] = useState({ id: '', name: '' });
  const [header, setHeader] = useState({
    invoiceNumber: '',
    vendorId: '',
    businessUnitId: '',
    vendorName: '',
    vendorTaxId: '',
    invoiceDate: '',
    invoiceDueDate: '',
    currency: 'USD',
    invoiceSubtotal: '',
    invoiceTotalTaxes: '',
    invoiceTotalFees: '',
    invoiceTotalAmountDue: '',
  });
  const [lines, setLines] = useState([emptyLine()]);

  const lockedBusinessUnitId = loadedBusinessUnit.id || selectedBusinessUnitId;
  const lockedBusinessUnitName = loadedBusinessUnit.name || selectedBusinessUnitName;

  const loadInvoiceForEdit = async () => {
    if (!invoiceId || !tenantId) return;
    try {
      setLoadingInvoice(true);
      const [headerData, linesData] = await Promise.all([
        apiFetch(`conductor/ap-invoice-headers/${invoiceId}`),
        apiFetch(`conductor/ap-invoice-lines/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${invoiceId}`),
      ]);

      setOriginalHeader(headerData);
      const buId = headerData.businessUnitId || '';
      const buMatch = businessUnits.find((bu) => bu.businessUnitId === buId);
      setLoadedBusinessUnit({ id: buId, name: buMatch?.businessUnitName || '' });
      setHeader({
        invoiceNumber: headerData.invoiceNumber || '',
        vendorId: headerData.vendorId || '',
        businessUnitId: buId,
        vendorName: headerData.vendorName || '',
        vendorTaxId: headerData.vendorTaxId || '',
        invoiceDate: toDateInput(headerData.invoiceDate),
        invoiceDueDate: toDateInput(headerData.invoiceDueDate),
        currency: headerData.currency || 'USD',
        invoiceSubtotal: headerData.invoiceSubtotal ?? '',
        invoiceTotalTaxes: headerData.invoiceTotalTaxes ?? '',
        invoiceTotalFees: headerData.invoiceTotalFees ?? '',
        invoiceTotalAmountDue: headerData.invoiceTotalAmountDue ?? '',
      });

      const loadedLines = Array.isArray(linesData) ? linesData : [];
      setOriginalLineIds(loadedLines.map((line) => line.apInvoiceLineId).filter(Boolean));
      setLines(
        loadedLines.length
          ? loadedLines.map((line) => ({
            apInvoiceLineId: line.apInvoiceLineId,
            lineItemDescription: line.lineItemDescription || '',
            quantity: line.quantity ?? '',
            lineItemRate: line.lineItemRate ?? '',
            lineItemTotal: line.lineItemTotal ?? '',
            productCode: line.productCode || '',
          }))
          : [emptyLine()]
      );
    } catch (err) {
      showError?.(err.message || 'Failed to load invoice');
    } finally {
      setLoadingInvoice(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (invoiceId) {
      loadInvoiceForEdit();
      return;
    }
    if (selectedBusinessUnitId) {
      setHeader((prev) => ({ ...prev, businessUnitId: selectedBusinessUnitId }));
    }
  }, [open, invoiceId, selectedBusinessUnitId]);

  // Subtotal = sum of line totals; Total = Subtotal + Tax + Shipping
  useEffect(() => {
    if (!open || loadingInvoice) return;
    const subtotal = sumLineTotals(lines);
    const tax = Number(header.invoiceTotalTaxes);
    const shipping = Number(header.invoiceTotalFees);
    const taxValue = Number.isFinite(tax) ? tax : 0;
    const shippingValue = Number.isFinite(shipping) ? shipping : 0;
    const total = roundMoney(subtotal + taxValue + shippingValue);
    const nextSubtotal = String(subtotal);
    const nextTotal = String(total);
    setHeader((prev) => {
      if (String(prev.invoiceSubtotal) === nextSubtotal && String(prev.invoiceTotalAmountDue) === nextTotal) {
        return prev;
      }
      return {
        ...prev,
        invoiceSubtotal: nextSubtotal,
        invoiceTotalAmountDue: nextTotal,
      };
    });
  }, [
    open,
    loadingInvoice,
    lines,
    header.invoiceTotalTaxes,
    header.invoiceTotalFees,
  ]);

  const resetForm = () => {
    setOriginalHeader(null);
    setOriginalLineIds([]);
    setLoadedBusinessUnit({ id: '', name: '' });
    setHeader({
      invoiceNumber: '',
      vendorId: '',
      businessUnitId: '',
      vendorName: '',
      vendorTaxId: '',
      invoiceDate: '',
      invoiceDueDate: '',
      currency: 'USD',
      invoiceSubtotal: '',
      invoiceTotalTaxes: '',
      invoiceTotalFees: '',
      invoiceTotalAmountDue: '',
    });
    setLines([emptyLine()]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const updateHeader = (field, value) => {
    setHeader((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'vendorId') {
        const vendor = vendors.find((v) => v.vendorId === value);
        if (vendor) {
          next.vendorName = vendor.vendorName || '';
          next.vendorTaxId = vendor.taxId || '';
        }
      }
      return next;
    });
  };

  const updateLine = (index, field, value) => {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        const next = { ...line, [field]: value };
        if (field === 'quantity' || field === 'lineItemRate') {
          const qtyRaw = next.quantity;
          const rateRaw = next.lineItemRate;
          const qty = Number(qtyRaw);
          const rate = Number(rateRaw);
          if (
            qtyRaw !== ''
            && rateRaw !== ''
            && Number.isFinite(qty)
            && Number.isFinite(rate)
          ) {
            next.lineItemTotal = String(Math.round(qty * rate * 100) / 100);
          } else {
            next.lineItemTotal = '';
          }
        }
        return next;
      })
    );
  };

  const addLine = () => {
    if (!(isEditMode ? canEdit : canAdd)) return;
    setLines((prev) => [...prev, emptyLine()]);
  };
  const removeLine = (index) => setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  const toNumber = (value) => (value === '' || value == null ? null : Number(value));

  const handleSave = async () => {
    if ((isEditMode && !canEdit) || (!isEditMode && !canAdd)) return;
    if (!header.invoiceNumber?.trim()) {
      showError?.('Invoice number is required');
      return;
    }
    const businessUnitId = header.businessUnitId || lockedBusinessUnitId;
    if (!businessUnitId) {
      showError?.('Select a business unit on the Dashboard before creating an invoice');
      return;
    }
    if (!tenantId) {
      showError?.('Tenant is required');
      return;
    }

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user_id');
      const authHeaders = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;

      const headerFields = {
        tenantId,
        invoiceNumber: header.invoiceNumber.trim(),
        vendorId: header.vendorId || null,
        businessUnitId,
        vendorName: header.vendorName || null,
        vendorTaxId: header.vendorTaxId || null,
        invoiceDate: header.invoiceDate || null,
        invoiceDueDate: header.invoiceDueDate || null,
        currency: header.currency || 'USD',
        invoiceSubtotal: toNumber(header.invoiceSubtotal),
        invoiceTotalTaxes: toNumber(header.invoiceTotalTaxes),
        invoiceTotalFees: toNumber(header.invoiceTotalFees),
        invoiceTotalAmountDue: toNumber(header.invoiceTotalAmountDue),
        updatedBy: userId || null,
      };

      let savedInvoiceId = invoiceId;

      if (isEditMode) {
        const headerPayload = {
          ...originalHeader,
          ...headerFields,
        };
        await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`, {
          method: 'PUT',
          body: JSON.stringify(headerPayload),
        });
      } else {
        const headerPayload = {
          ...headerFields,
          workflowStatus: 'DRAFT',
          paymentStatus: 'UNPAID',
          createdBy: userId || null,
        };

        const headerRes = await fetch(`${base}conductor/ap-invoice-headers`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(headerPayload),
        });
        if (!headerRes.ok) {
          const err = await headerRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to create invoice header');
        }
        const createdHeader = await headerRes.json();
        savedInvoiceId = createdHeader.apInvoiceId;
      }

      const validLines = lines.filter((l) => l.lineItemDescription?.trim());
      const keptLineIds = new Set();

      for (let i = 0; i < validLines.length; i += 1) {
        const line = validLines[i];
        const linePayload = {
          tenantId,
          apInvoiceId: savedInvoiceId,
          invoiceNumber: header.invoiceNumber.trim(),
          lineItemNumber: String(i + 1),
          lineItemDescription: line.lineItemDescription.trim(),
          quantity: toNumber(line.quantity),
          lineItemRate: toNumber(line.lineItemRate),
          lineItemTotal: toNumber(line.lineItemTotal),
          productCode: line.productCode || null,
          businessUnitId,
          updatedBy: userId || null,
        };

        if (isEditMode && line.apInvoiceLineId) {
          keptLineIds.add(line.apInvoiceLineId);
          await apiFetch(`conductor/ap-invoice-lines/${line.apInvoiceLineId}`, {
            method: 'PUT',
            body: JSON.stringify({ ...linePayload, apInvoiceLineId: line.apInvoiceLineId }),
          });
        } else {
          const createdLine = await apiFetch('conductor/ap-invoice-lines', {
            method: 'POST',
            body: JSON.stringify({
              ...linePayload,
              createdBy: userId || null,
            }),
          });
          if (createdLine?.apInvoiceLineId) keptLineIds.add(createdLine.apInvoiceLineId);
        }
      }

      if (isEditMode) {
        for (const lineId of originalLineIds) {
          if (!keptLineIds.has(lineId)) {
            await apiFetch(`conductor/ap-invoice-lines/${lineId}`, { method: 'DELETE' });
          }
        }
      }

      showSuccess?.(isEditMode ? 'Invoice updated successfully' : 'Invoice created successfully');
      onSaved?.(savedInvoiceId);
      handleClose();
    } catch (err) {
      showError?.(err.message || `Failed to ${isEditMode ? 'update' : 'save'} invoice`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
      <DialogTitle sx={harmonateDialogTitleSx}>
        {isEditMode ? 'Edit Invoice' : 'Manual Invoice Entry'}
        <IconButton onClick={handleClose} size="small" sx={harmonateDialogCloseIconSx}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loadingInvoice ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
        <>
        <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mb: 1.5 }}>Header Data</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Invoice Number" fullWidth required size="small" value={header.invoiceNumber}
              onChange={(e) => updateHeader('invoiceNumber', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Currency" fullWidth size="small" value={header.currency}
              onChange={(e) => updateHeader('currency', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor"
              fullWidth
              select
              size="small"
              value={header.vendorId}
              onChange={(e) => updateHeader('vendorId', e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Select vendor</MenuItem>
              {vendors.map((v) => (
                <MenuItem key={v.vendorId} value={v.vendorId}>{v.vendorName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            {lockedBusinessUnitId ? (
              <TextField
                label="Business Unit"
                fullWidth
                size="small"
                value={lockedBusinessUnitName || lockedBusinessUnitId}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <TextField
                label="Business Unit"
                fullWidth
                select
                size="small"
                value={header.businessUnitId}
                onChange={(e) => updateHeader('businessUnitId', e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Select business unit</MenuItem>
                {businessUnits.map((bu) => (
                  <MenuItem key={bu.businessUnitId} value={bu.businessUnitId}>{bu.businessUnitName}</MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Vendor Name" fullWidth size="small" value={header.vendorName}
              onChange={(e) => updateHeader('vendorName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Tax ID" fullWidth size="small" value={header.vendorTaxId}
              onChange={(e) => updateHeader('vendorTaxId', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Invoice Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
              value={header.invoiceDate} onChange={(e) => updateHeader('invoiceDate', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Due Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
              value={header.invoiceDueDate} onChange={(e) => updateHeader('invoiceDueDate', e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={harmonateSectionTitleSx}>Line Items</Typography>
          <Button startIcon={<AddIcon />} onClick={addLine} disabled={!(isEditMode ? canEdit : canAdd)} sx={harmonateAccentLinkSx}>Add Line</Button>
        </Box>
        {lines.map((line, index) => (
          <Grid container spacing={1.5} key={`line-${index}`} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField label="Description" fullWidth size="small" value={line.lineItemDescription}
                onChange={(e) => updateLine(index, 'lineItemDescription', e.target.value)} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField label="Qty" fullWidth size="small" type="number" value={line.quantity}
                onChange={(e) => updateLine(index, 'quantity', e.target.value)} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField label="Unit Price" fullWidth size="small" type="number" value={line.lineItemRate}
                onChange={(e) => updateLine(index, 'lineItemRate', e.target.value)} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Line Total"
                fullWidth
                size="small"
                type="number"
                value={line.lineItemTotal}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={10} sm={1.5}>
              <TextField label="Part/SKU" fullWidth size="small" value={line.productCode}
                onChange={(e) => updateLine(index, 'productCode', e.target.value)} />
            </Grid>
            <Grid item xs={2} sm={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton size="small" onClick={() => removeLine(index)} disabled={!(isEditMode ? canDelete || canEdit : canAdd)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Grid>
          </Grid>
        ))}

        <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mt: 2.5, mb: 1.5 }}>Financials</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Subtotal"
              fullWidth
              size="small"
              type="number"
              value={header.invoiceSubtotal}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Tax Amount" fullWidth size="small" type="number" value={header.invoiceTotalTaxes}
              onChange={(e) => updateHeader('invoiceTotalTaxes', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Shipping/Freight" fullWidth size="small" type="number" value={header.invoiceTotalFees}
              onChange={(e) => updateHeader('invoiceTotalFees', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Total Amount"
              fullWidth
              size="small"
              type="number"
              value={header.invoiceTotalAmountDue}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
        </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || (isEditMode ? !canEdit : !canAdd)}
          disabled={submitting || loadingInvoice}
          sx={harmonateProceedButtonSx}
        >
          {submitting ? 'Saving...' : (isEditMode ? 'Update Invoice' : 'Save Invoice')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceManualEntryDialog;
